import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { FetchGetInfiniteSearchResultDesktopLoadService } from '@/packages/query-state/shared/store/search-offers/v2/get-infinite-search-result-desktop/load';

import { type TGetInfiniteSearchResultDesktopResponse_1 } from '@/repositories/generated/shared/search-offers/v2/get-infinite-search-result-desktop';

import { type TCardApiContext } from '../../types/TCardApiContext';
import { type TCardContextData } from '../../types/TCardContextData';

import { useRefetchClusters } from './internal/hooks/useRefetchClusters';
import { getPageAndOfferIndexForInfiniteData } from './internal/utils/getPageAndOfferIndexForInfiniteData';
import { type InitCardApiContext } from './types';

export function useInitSimilarOffersCardApi(): InitCardApiContext {
  const queryClient = useQueryClient();
  const fetchGetSimilarOffersLoadService = useMemo(
    () => new FetchGetInfiniteSearchResultDesktopLoadService(queryClient),
    [queryClient],
  );

  const refetchClustersOnSuccess = useRefetchClusters();

  return useCallback<(cardContextData: TCardContextData) => TCardApiContext>(
    () => ({
      hideOfferMutationOptions: {
        onError(_error, _variables, context: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null): void {
          if (context) {
            fetchGetSimilarOffersLoadService.setQueryData(context);
          }
        },
        onMutate(params): InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null {
          const prev = fetchGetSimilarOffersLoadService.getQueryData();

          if (prev) {
            const next: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> = JSON.parse(JSON.stringify(prev));
            const { pageIndex, offerIndex } = getPageAndOfferIndexForInfiniteData(next, params.offerId);

            if (pageIndex !== null && offerIndex !== null) {
              next.pages[pageIndex].offers.splice(offerIndex, 1);

              fetchGetSimilarOffersLoadService.setQueryData(next);
            }
          }

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
      addToFavoriteMutationOptions: {
        onError(_error, _variables, context: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null): void {
          if (context) {
            fetchGetSimilarOffersLoadService.setQueryData(context);
          }
        },
        onMutate(params): InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null {
          const prev = fetchGetSimilarOffersLoadService.getQueryData();

          if (prev) {
            const next: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> = JSON.parse(JSON.stringify(prev));
            const { pageIndex, offerIndex } = getPageAndOfferIndexForInfiniteData(next, params.entityId);

            if (pageIndex !== null && offerIndex !== null) {
              next.pages[pageIndex].offers[offerIndex].data.extendedProperties.isFavorite = true;

              fetchGetSimilarOffersLoadService.setQueryData(next);
            }
          }

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
      deleteFromFavoriteMutationOptions: {
        onError(_error, _variables, context): void {
          if (context) {
            fetchGetSimilarOffersLoadService.setQueryData(context);
          }
        },
        onMutate(params): InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null {
          const prev = fetchGetSimilarOffersLoadService.getQueryData();

          if (prev) {
            const next: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> = JSON.parse(JSON.stringify(prev));
            const { pageIndex, offerIndex } = getPageAndOfferIndexForInfiniteData(next, params.entityId);

            if (pageIndex !== null && offerIndex !== null) {
              next.pages[pageIndex].offers[offerIndex].data.extendedProperties.isFavorite = false;

              fetchGetSimilarOffersLoadService.setQueryData(next);
            }
          }

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
      addToComparisonMutationOptions: {
        onError(_error, _variables, context: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null): void {
          if (context) {
            fetchGetSimilarOffersLoadService.setQueryData(context);
          }
        },
        onMutate(params): InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null {
          const prev = fetchGetSimilarOffersLoadService.getQueryData();

          if (prev) {
            const next: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> = JSON.parse(JSON.stringify(prev));
            const { pageIndex, offerIndex } = getPageAndOfferIndexForInfiniteData(next, params.offerId);

            if (pageIndex !== null && offerIndex !== null) {
              next.pages[pageIndex].offers[offerIndex].data.comparisonStatus = {
                description: 'Добавлено в сравнение',
                status: 'added',
              };

              fetchGetSimilarOffersLoadService.setQueryData(next);
            }
          }

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
      deleteFromComparisonMutationOptions: {
        onError(_error, _variables, context): void {
          if (context) {
            fetchGetSimilarOffersLoadService.setQueryData(context);
          }
        },
        onMutate(params): InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> | null {
          const prev = fetchGetSimilarOffersLoadService.getQueryData();

          if (prev) {
            const next: InfiniteData<TGetInfiniteSearchResultDesktopResponse_1> = JSON.parse(JSON.stringify(prev));
            const { pageIndex, offerIndex } = getPageAndOfferIndexForInfiniteData(next, params.offerId);

            if (pageIndex !== null && offerIndex !== null) {
              next.pages[pageIndex].offers[offerIndex].data.comparisonStatus = {
                description: 'Добавить к сравнению',
                status: 'available',
              };

              fetchGetSimilarOffersLoadService.setQueryData(next);
            }
          }

          return prev;
        },
        onSuccess: refetchClustersOnSuccess,
      },
    }),
    [fetchGetSimilarOffersLoadService, refetchClustersOnSuccess],
  );
}
